import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Users, FileText, PenTool, CheckCircle, Clock, List, Send, Scale, AlertTriangle, UserCheck, UserX } from 'lucide-react';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await adminAPI.dashboard();
        setData(res.data);
      } catch { toast.error('Erro ao carregar dashboard'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:32,height:32}} /></div>;

  const c = data?.clientes || {};
  const l = data?.listas || {};

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-2xl font-semibold">Visao Geral</h1>
        <p className="text-sm text-white/50">Metricas canonicas do sistema</p>
      </div>

      {/* SEÇÃO A — CLIENTES */}
      <Section title="Clientes">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card icon={Users} label="Cadastrados" value={c.cadastrados} />
          <Card icon={FileText} label="Docs Enviados" value={c.docsEnviados} color="green" />
          <Card icon={Clock} label="Docs Pendentes" value={c.docsPendentes} color="warning" />
          <Card icon={PenTool} label="Termo Assinado" value={c.termoAssinado} color="green" />
          <Card icon={Clock} label="Termo Pendente" value={c.termoPendente} color="warning" />
        </div>
      </Section>

      {/* SEÇÃO B — PIPELINE CLIENTES */}
      <Section title="Pipeline de Clientes">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card icon={UserCheck} label="Aptos p/ Lista" value={c.aptosParaLista} color="green" />
          <Card icon={List} label="Em Lista" value={c.emLista} />
          <Card icon={UserX} label="Aguardando Lista" value={c.aguardandoLista} color="warning" />
        </div>
      </Section>

      {/* SEÇÃO C — LISTAS */}
      <Section title="Listas (Lotes)">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card icon={List} label="Total" value={l.total} />
          <Card icon={FileText} label="Geradas" value={l.created} />
          <Card icon={Send} label="Enviadas" value={l.sent} color="warning" />
          <Card icon={Scale} label="Protocoladas" value={l.protocolled} color="green" />
          <Card icon={Clock} label="Aguard. Baixas" value={l.aguardandoBaixas} color="warning" />
          <Card icon={AlertTriangle} label="Baixas Andamento" value={l.baixasEmAndamento} color="warning" />
          <Card icon={CheckCircle} label="Concluidas" value={l.baixasConcluidas} color="green" />
        </div>
      </Section>

      {/* ATIVIDADE RECENTE */}
      <div className="glass-card">
        <h3 className="font-display text-base font-semibold mb-4">Atividade Recente</h3>
        <div className="space-y-2">
          {(!data?.recentActivity || data.recentActivity.length === 0) && (
            <p className="text-xs text-white/30 text-center py-4">Sem atividades recentes.</p>
          )}
          {data?.recentActivity?.map((ms, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.03] transition-colors">
              <div className="w-1.5 h-1.5 rounded-full" style={{background:'var(--rn-green)'}} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white/70">
                  <span className="font-medium text-white/85">{ms.user?.name || 'Cliente'}</span>
                  {' — '}{milestoneLabel(ms.type)}
                </p>
                <p className="text-[10px] text-white/30">{ms.case?.docFormatted}</p>
              </div>
              <span className="text-[10px] text-white/25 whitespace-nowrap">
                {ms.occurredAt ? new Date(ms.occurredAt).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-xs text-white/40 uppercase tracking-wider font-semibold mb-2">{title}</h2>
      {children}
    </div>
  );
}

function Card({ icon: Icon, label, value, color }) {
  const colors = { green: 'var(--rn-green)', warning: 'var(--rn-warning)', danger: '#D32F2F' };
  const ic = colors[color] || 'rgba(255,255,255,0.35)';
  return (
    <div className="glass-card !p-3.5">
      <div className="flex items-center justify-between mb-1.5">
        <Icon size={16} style={{color: ic}} />
      </div>
      <p className="font-display text-xl font-semibold tabular-nums">{value ?? 0}</p>
      <p className="text-[10px] text-white/40 mt-0.5">{label}</p>
    </div>
  );
}

function milestoneLabel(type) {
  const m = {
    REGISTERED:'Cadastro', DOCUMENT_UPLOADED:'Doc enviado', DOCUMENT_APPROVED:'Doc validado',
    TERM_SIGNED:'Termo assinado', PROTOCOL_SENT:'Protocolo enviado', ACTION_FILED:'Acao protocolada',
    NEG_SPC_CLEARED:'Baixa SPC', NEG_BOA_VISTA_CLEARED:'Baixa Boa Vista',
    NEG_CENPROT_CLEARED:'Baixa Cenprot', NEG_SERASA_CLEARED:'Baixa Serasa',
  };
  return m[type] || type;
}
